"use client"

import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { TOWER_ANIMATIONS } from "@/constants/animations"
import { debug, error } from "@/lib/debug"
import { motion, useAnimation, AnimatePresence } from "framer-motion"
import { COLLECTIONS } from "@/lib/api"
import { getCubeImageForPreview } from "@/lib/seasonal-images"
import { useMobile } from "@/hooks/use-mobile"
import { performEasterEggAnimation } from "@/lib/easter-egg-animation"
import type { TowerCube, RandomTowerProps } from "@/types/tower"

export interface RandomTowerRef {
  /** Build a new random tower */
  generateRandomTower: () => void
  /** Shuffle the tower with the Easter‑egg animation */
  activateEasterEgg: (version?: 1 | 2) => void
}

const MAX_CUBES = 10 // for pre‑allocating animation controls
const BASE_Z = 10 // baseline z‑index
const SPACING_RATIO = 0.56 // vertical overlap

// Use animation constants from shared file
const ANIMATION_CONFIG = TOWER_ANIMATIONS.DROP
const MOBILE_ANIMATION_CONFIG = TOWER_ANIMATIONS.MOBILE

// Placeholder for performEasterEggAnimationV2, replace with actual implementation if available
const performEasterEggAnimationV2 = async ({
  tower,
  controlsRef,
  cubeSize,
  includeBaseCube,
  getPositions,
  getZ,
  cubeFlipStates,
}: any) => {
  // Implement the v2 animation logic here
  console.warn("performEasterEggAnimationV2 is a placeholder. Implement the actual animation.")
  return tower // Return the tower as is for now
}

const RandomTower = forwardRef<RandomTowerRef, RandomTowerProps>(
  (
    {
      isMobileView = false,
      includeBaseCube = !isMobileView,
      randomCubeRange = { min: 2, max: 4 },
      onHeightChange,
      simplifiedAnimation = false,
      alignToTop = false, // New prop to align to top
    }: RandomTowerProps,
    ref,
  ) => {
    // Call all hooks unconditionally
    const isMobile = useMobile()
    const cubeSize = isMobileView ? 112 : isMobile ? 200 : 212

    const [tower, setTower] = useState<TowerCube[]>([])
    const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({})
    const [isAnimating, setIsAnimating] = useState(false) // Animation lock
    const [isFirstRender, setIsFirstRender] = useState(true) // Track first render
    const [shouldAnimateDrop, setShouldAnimateDrop] = useState(false) // Control drop animation
    const [easterEggVersion, setEasterEggVersion] = useState<1 | 2>(1) // Track easter egg version
    const [isMounted, setIsMounted] = useState(false)

    // Add a ref to track the flip state of each cube
    const cubeFlipStates = useRef<Record<string, boolean>>({})

    // Add clickCount state and timeout ref
    const [clickCount, setClickCount] = useState(0)
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    /* ------------------------------------------------------------------
     *  POSITION & Z‑INDEX HELPERS
     * ---------------------------------------------------------------- */

    const getPositions = (count: number) => {
      const positions: number[] = []
      for (let i = 0; i < count; i++) {
        if (i === 0) positions.push(0)
        else if (i === 1) positions.push(Math.round(cubeSize * SPACING_RATIO))
        else if (i === 2) positions.push(Math.round(cubeSize * SPACING_RATIO * 2))
        else if (i === 3) positions.push(Math.round(cubeSize * SPACING_RATIO * 3 + cubeSize * 0.04))
        else if (i === 4) positions.push(Math.round(cubeSize * SPACING_RATIO * 4 + cubeSize * 0.08))
        else positions.push(Math.round(cubeSize * SPACING_RATIO * i + cubeSize * 0.1))
      }
      return positions
    }

    const positions = getPositions(tower.length)
    const stackHeight = positions.length ? positions[positions.length - 1] + cubeSize : cubeSize

    /* ------------------------------------------------------------------
     *  ANIMATION CONTROLS (single ref per cube for *all* properties including z‑index)
     * ---------------------------------------------------------------- */
    const controlsRef = useRef([...Array(MAX_CUBES)].map(() => useAnimation()))

    const getZ = (verticalIndex: number) => BASE_Z + verticalIndex

    // Update the handleImageError function to use the new image paths
    const handleImageError = (cubeId: string) => {
      error(`Failed to load image for cube: ${cubeId}`)
      setImageErrors((prev) => ({
        ...prev,
        [cubeId]: true,
      }))

      // Try to reload the image with a different path format if possible
      const cube = tower.find((c) => c.id === cubeId)
      if (cube) {
        const altPath = `/images/${cubeId}-cube.webp`
        if (cube.image !== altPath) {
          cube.image = altPath
        }
      }
    }

    /* ------------------------------------------------------------------
     *  BUILD / SHUFFLE LOGIC
     * ---------------------------------------------------------------- */

    const buildTowerArray = (randomCount: number) => {
      const shuffled = [...COLLECTIONS].sort(() => 0.5 - Math.random()).slice(0, randomCount)
      const arr: TowerCube[] = []
      if (includeBaseCube) {
        arr.push({
          id: "base-cube",
          name: "Base Cube",
          image: "/images/base-cube.webp",
          key: `base-cube-${Date.now()}`,
        })
      }
      shuffled.forEach((col, i) => {
        const key = `${col.id}-${i}-${Date.now()}`
        // Get seasonal image for preview if active, otherwise use default
        const imagePath = getCubeImageForPreview(col.id)
        arr.push({ id: col.id, name: col.name, image: imagePath, key })

        // Initialize flip state for this cube (unflipped by default)
        cubeFlipStates.current[key] = false
      })
      return arr
    }

    const generateRandomTower = () => {
      // Don't generate a new tower if animation is in progress
      if (isAnimating) return

      setIsAnimating(true)

      // First, clear the existing tower completely
      setTower([])

      // Clear all animation controls
      controlsRef.current.forEach((control) => {
        control.stop()
      })

      // Clear previous flip states
      cubeFlipStates.current = {}

      // Short delay to ensure old cubes are gone
      setTimeout(() => {
        // Generate the new tower
        const randomCount = Math.floor(
          Math.random() * (randomCubeRange.max - randomCubeRange.min + 1) + randomCubeRange.min,
        )
        const nextTower = buildTowerArray(randomCount)

        // Set the new tower with drop animation enabled
        setShouldAnimateDrop(true)
        setTower(nextTower)

        // Release animation lock after animation completes
        const animationDuration =
          simplifiedAnimation || isMobile
            ? nextTower.length * MOBILE_ANIMATION_CONFIG.fadeDelay * 1000 +
              MOBILE_ANIMATION_CONFIG.fadeDuration * 1000 +
              100
            : nextTower.length * ANIMATION_CONFIG.dropDelay * 1000 + ANIMATION_CONFIG.dropDuration * 1000 + 100

        setTimeout(() => {
          setIsAnimating(false)
          setShouldAnimateDrop(false)
        }, animationDuration)
      }, 50) // Short delay to ensure old cubes are removed
    }

    useEffect(() => {
      // Initialize animation controls for each cube
      tower.forEach((cube, idx) => {
        // Set initial positions for animation controls
        controlsRef.current[idx].start({
          x: 0,
          scaleX: cubeFlipStates.current[cube.key] ? -1 : 1,
          bottom: positions[idx],
          zIndex: getZ(idx),
          opacity: 1,
          rotateY: 0,
          transition: { duration: 0 },
        })
      })
    }, [tower])

    const activateEasterEgg = async (version?: 1 | 2) => {
      // Animation lock to prevent multiple activations
      if (isAnimating || tower.length <= 2) return
      setIsAnimating(true)

      try {
        let newTower: TowerCube[]

        // Use the specified version or default to v1
        const animationVersion = version || 1

        if (animationVersion === 1) {
          // Use the v1 animation (side-step)
          newTower = await performEasterEggAnimation({
            tower,
            controlsRef,
            cubeSize,
            includeBaseCube,
            getPositions,
            getZ,
            cubeFlipStates,
          })
        } else {
          // Use the v2 animation (in-place rotation)
          newTower = await performEasterEggAnimationV2({
            tower,
            controlsRef,
            cubeSize,
            includeBaseCube,
            getPositions,
            getZ,
            cubeFlipStates,
          })
        }

        // Update the tower state with the new array
        setTower(newTower)
      } catch (error) {
        debug(`Error during Easter Egg animation:`, error)
      } finally {
        // Release animation lock
        setIsAnimating(false)
      }
    }

    /* ------------------------------------------------------------------ */

    useImperativeHandle(
      ref,
      () => ({
        generateRandomTower,
        activateEasterEgg,
      }),
      [tower, isAnimating],
    )

    useEffect(() => {
      generateRandomTower()
      // Mark first render as complete after a short delay
      const timer = setTimeout(() => {
        setIsFirstRender(false)
      }, 500)

      return () => clearTimeout(timer)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      onHeightChange?.(stackHeight)
    }, [stackHeight, onHeightChange])

    useEffect(() => {
      return () => {
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current)
        }
      }
    }, [])

    /* ------------------------------------------------------------------ */

    const handleBaseClick = () => {
      // Increment click count
      const newClickCount = clickCount + 1
      setClickCount(newClickCount)

      // Clear any existing timeout
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
      }

      // Set a new timeout to reset click count after 500ms
      clickTimeoutRef.current = setTimeout(() => {
        setClickCount(0)
      }, 500)

      // If triple-click detected, activate Easter Egg
      if (newClickCount === 3) {
        setClickCount(0)
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current)
        }
        activateEasterEgg()
      }
    }

    // Function to calculate the animation batch
    const getAnimationBatch = (index: number) => {
      return index
    }

    // Determine if we should use simplified animations
    const useSimplifiedAnimation = simplifiedAnimation || isMobile

    useEffect(() => {
      const handleResize = () => {
        setIsMounted(true)
      }

      setIsMounted(true)
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }, [])

    return (
      /* Outer container - creates positioning context and centers content */
      <div className={`relative w-full h-full flex ${alignToTop ? "items-start" : "items-center"} justify-center`}>
        {/* Tower container - holds all cubes with specific dimensions and positioning */}
        <div
          className="flex flex-col items-center justify-center"
          style={{
            position: "absolute",
            width: `${cubeSize}px`,
            height: `${stackHeight}px`,
            transition: "height 0.4s ease, top 0.4s ease, transform 0.4s ease",
            // If alignToTop is true, position at the top; otherwise center vertically
            top: alignToTop ? 0 : "50%",
            transform: alignToTop ? "none" : "translateY(-50%)",
          }}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {/* Individual cube elements */}
            {tower.map((cube, idx) => {
              // Get the current flip state for this cube
              const isFlipped = cubeFlipStates.current[cube.key] || false

              // Calculate animation batch for staggered animation
              const batch = getAnimationBatch(idx)

              // Use different animation variants based on device/preference
              const variants = useSimplifiedAnimation
                ? {
                    // Simplified animation for mobile - no position changes, just opacity and scale
                    initial: {
                      x: 0,
                      bottom: positions[idx],
                      scaleX: isFlipped ? -1 : 1,
                      scale: MOBILE_ANIMATION_CONFIG.scaleFrom,
                      opacity: 0,
                      zIndex: getZ(idx),
                    },
                    animate: {
                      x: 0,
                      bottom: positions[idx],
                      scaleX: isFlipped ? -1 : 1,
                      scale: 1,
                      opacity: 1,
                      zIndex: getZ(idx),
                      transition: {
                        duration: MOBILE_ANIMATION_CONFIG.fadeDuration,
                        delay: batch * MOBILE_ANIMATION_CONFIG.fadeDelay,
                        ease: "easeOut",
                      },
                    },
                    exit: {
                      opacity: 0,
                      scale: MOBILE_ANIMATION_CONFIG.scaleFrom,
                      transition: { duration: 0.2 },
                    },
                  }
                : {
                    // Original animation for desktop
                    initial: {
                      x: 0,
                      bottom: positions[idx] + ANIMATION_CONFIG.dropHeight,
                      scaleX: isFlipped ? -1 : 1,
                      opacity: 0,
                      zIndex: getZ(idx),
                      rotateY: 0,
                    },
                    animate: {
                      x: 0,
                      bottom: positions[idx],
                      scaleX: isFlipped ? -1 : 1,
                      opacity: 1,
                      zIndex: getZ(idx),
                      rotateY: 0,
                      transition: {
                        type: "spring",
                        stiffness: ANIMATION_CONFIG.springStiffness,
                        damping: ANIMATION_CONFIG.springDamping,
                        mass: ANIMATION_CONFIG.springMass,
                        delay: batch * ANIMATION_CONFIG.dropDelay,
                        opacity: { duration: 0.2, delay: batch * ANIMATION_CONFIG.dropDelay },
                      },
                    },
                    exit: {
                      opacity: 0,
                      transition: { duration: 0.1 },
                    },
                  }

              return (
                <motion.div
                  key={cube.key}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={variants}
                  onClick={idx === 0 && includeBaseCube ? handleBaseClick : undefined}
                  style={{
                    position: "absolute",
                    width: `${cubeSize}px`,
                    height: `${cubeSize}px`,
                    transform: "translateX(-50%)",
                  }}
                >
                  <OptimizedImage
                    src={imageErrors[cube.id] ? `/placeholder.svg?height=${cubeSize}&width=${cubeSize}` : cube.image}
                    alt={cube.name}
                    width={cubeSize}
                    height={cubeSize}
                    className="object-contain"
                    fallbackFormats={["webp", "png", "jpg", "jpeg"]}
                    placeholderText={cube.name}
                    priority={idx < 3}
                  />
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      </div>
    )
  },
)

RandomTower.displayName = "RandomTower"
export { RandomTower }
